// Shared ATTACHMENT PICKER for the task windows (Board's New task dialog, the
// task window's edit mode, Feed's Add to Board, the request dialogs) — one
// implementation of the attach contract. Holds SEVERAL files (the backend
// accepts up to 5 per request).
//
// ONE path for every file type: bytes upload to S3 via the chat-upload
// presigned-PUT flow (on submit) and ride on the request as `attachments`
// references. The old second path — inlining TEXT files into the brief —
// was removed deliberately: the fleet skills already treat `attachments`
// as the AUTHORITATIVE input (build-cortex-project: "do NOT generate a
// synthetic dataset for tables the attachments cover"), the classifier
// only ever receives attachment NAMES, and inlining made the document an
// editable part of the brief with a silent 50k-char truncation. A file is
// a file now — visible as the 📎 chip / Attachments section everywhere.
import { useRef } from "react";
import { Paperclip, X } from "lucide-react";
import { getUploadSignedUrl } from "../../services/pipeline";
import { btnGhost } from "./requestMeta";

export const FILE_ACCEPT = ".txt,.md,.csv,.docx,.pdf,.zip,.xlsx,.xls,.png,.jpg,.jpeg,.gif,.webp,.rdl,.rdlc";
const MAX_BYTES = 200 * 1024 * 1024;
// Mirrors the backend's ATTACHMENT_MAX_COUNT (POST /api/app_requests).
export const MAX_FILES = 5;

const fmtSize = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

// Turn the picked files into createAppRequest fields. Call on SUBMIT — the
// uploads happen right here (that is why the picked rows say "uploads on
// submit"). Accepts an array or a single file (legacy callers). Returns {}
// or { attachments: [...] }; throws on upload failure so the caller can
// surface it and NOT file the request.
export async function prepareAttachments(files) {
  const list = (Array.isArray(files) ? files : [files]).filter((f) => f?.blob);
  if (!list.length) return {};
  const attachments = [];
  for (const file of list) {
    const contentType = file.blob.type || "application/octet-stream";
    const signed = await getUploadSignedUrl(file.name, contentType);
    if (!signed?.url || !signed?.key) throw new Error("upload sign failed");
    const put = await fetch(signed.url, { method: "PUT", headers: { "Content-Type": contentType }, body: file.blob });
    if (!put.ok) throw new Error(`upload failed (HTTP ${put.status})`);
    attachments.push({ key: signed.key, s3_uri: signed.s3_uri, name: file.name, size: file.size, content_type: contentType });
  }
  return { attachments };
}

// Back-compat alias (single file) — same contract, one file.
export const prepareAttachment = prepareAttachments;

// Controlled: the caller owns the files state (array of { name, size, blob })
// so its submit handler can read it; onError receives a message string or
// null (pick succeeded) for the caller's error area.
export default function AttachmentPicker({ files = [], onChange, onError, hint, testId = "attachment-file", max = MAX_FILES }) {
  const fileInput = useRef(null);
  const list = Array.isArray(files) ? files : (files ? [files] : []);
  const pickFiles = (picked) => {
    const incoming = Array.from(picked || []);
    if (!incoming.length) return;
    const next = [...list];
    for (const f of incoming) {
      if (f.size > MAX_BYTES) { onError?.(`${f.name}: file too large (max ${fmtSize(MAX_BYTES)})`); return; }
      if (next.some((x) => x.name === f.name && x.size === f.size)) continue; // same file picked twice
      next.push({ name: f.name, size: f.size, blob: f });
    }
    if (next.length > max) { onError?.(`at most ${max} files per request`); return; }
    onChange(next);
    onError?.(null);
  };
  const removeAt = (i) => {
    const next = list.filter((_, idx) => idx !== i);
    onChange(next);
    if (fileInput.current) fileInput.current.value = "";
  };
  return (
    <div className="flex flex-col gap-1.5">
      <input
        ref={fileInput}
        type="file"
        multiple
        accept={FILE_ACCEPT}
        className="hidden"
        data-testid={testId}
        onChange={(e) => { pickFiles(e.target.files); e.target.value = ""; }}
      />
      {list.map((file, i) => (
        <div key={file.name + ":" + file.size} className="flex items-center gap-2 text-xs text-page-text">
          <Paperclip size={13} className="text-page-muted" />
          <span className="truncate">{file.name}</span>
          <span className="text-page-muted shrink-0">{fmtSize(file.size)} · uploads on submit</span>
          <button
            type="button"
            className="text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-0.5"
            onClick={() => removeAt(i)}
          >
            <X size={12} />
          </button>
        </div>
      ))}
      {list.length < max && (
        <button type="button" className={btnGhost + " text-xs self-start"} onClick={() => fileInput.current?.click()}>
          <Paperclip size={13} /> {list.length
            ? "Attach another file"
            : (hint || "Attach documents, data files, images, or archives (.pdf, .docx, .txt, .md, .csv, .xlsx, .zip, .png, .jpg)")}
        </button>
      )}
    </div>
  );
}
